// Thin client for the cve-api backend at /cve/api/*.
//
// Identity model: device-id stored in localStorage (never sent in HTML),
// posted once to /auth/anon → server returns a signed token we cache in
// localStorage and send as Authorization: Bearer on every request. No
// passwords, no email. Players upgrade to a recoverable account later.
//
// Failure mode: every public function returns null on any error. Callers
// must handle the null path (typically: fall back to local AI). This
// matches the design intent — the game stays playable offline.

import type { Card } from '../game/data/cards/types';
import type { HeroId } from '../game/data/heroes';
import type { CombatResult } from '../game/combatEngine';

const TOKEN_KEY = 'cve.v2.async.token';
const PLAYER_ID_KEY = 'cve.v2.async.playerId';
const DEVICE_ID_KEY = 'cve.v2.async.deviceId';

function apiBase(): string {
  // In dev, vite proxies /cve/api → live VPS by default. In prod, the SPA
  // and API share Caddy at /cve/. Both resolve to a relative URL. The Android
  // app runs at https://localhost, where a relative URL reaches nothing, so it
  // names the server outright; cve-api answers any origin because the token
  // rides in a header, not a cookie.
  if (import.meta.env.MODE === 'app') return 'https://raulcontrerasherrera.com/cve/api';
  return `${import.meta.env.BASE_URL}api`;
}

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (id && id.length >= 16) return id;
  // 16 random bytes → 22-char base64url. Stable per browser profile.
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  id = btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  localStorage.setItem(DEVICE_ID_KEY, id);
  return id;
}

interface AuthState {
  playerId: string;
  token: string;
}

let cached: AuthState | null = null;
let inflight: Promise<AuthState | null> | null = null;

function readCachedAuth(): AuthState | null {
  if (cached) return cached;
  const token = localStorage.getItem(TOKEN_KEY);
  const playerId = localStorage.getItem(PLAYER_ID_KEY);
  if (token && playerId) {
    cached = { token, playerId };
    return cached;
  }
  return null;
}

// Ensures we have a valid token. Returns null on network/server error so
// callers can degrade to offline mode without exceptions bubbling.
async function ensureAuth(handle?: string): Promise<AuthState | null> {
  const existing = readCachedAuth();
  if (existing) return existing;
  if (inflight) return inflight;

  const deviceId = getOrCreateDeviceId();
  inflight = (async () => {
    try {
      const res = await fetch(`${apiBase()}/auth/anon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId, handle }),
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (typeof json?.playerId !== 'string' || typeof json?.token !== 'string') {
        return null;
      }
      const auth = { playerId: json.playerId, token: json.token };
      localStorage.setItem(TOKEN_KEY, auth.token);
      localStorage.setItem(PLAYER_ID_KEY, auth.playerId);
      cached = auth;
      return auth;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response | null> {
  const auth = await ensureAuth();
  if (!auth) return null;
  try {
    return await fetch(`${apiBase()}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
        ...(init.headers || {}),
      },
    });
  } catch {
    return null;
  }
}

export interface PublishSnapshotInput {
  roundNum: number;
  heroId: HeroId;
  tavernTier: number;
  health: number;
  handle: string;
  board: Card[];
}

export async function publishSnapshot(input: PublishSnapshotInput): Promise<string | null> {
  const res = await authedFetch('/snapshots', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res || !res.ok) return null;
  try {
    const json = await res.json();
    return typeof json?.snapshotId === 'string' ? json.snapshotId : null;
  } catch {
    return null;
  }
}

export interface OpponentSnapshot {
  id: string;
  playerId: string;
  handle: string;
  heroId: string;
  tavernTier: number;
  health: number;
  board: Card[];
}

export async function fetchOpponentSnapshot(
  round: number,
  excludePlayerIds: readonly string[] = [],
): Promise<OpponentSnapshot | null> {
  const qs = new URLSearchParams({ round: String(round) });
  if (excludePlayerIds.length > 0) {
    qs.set('exclude', excludePlayerIds.join(','));
  }
  const res = await authedFetch(`/snapshots/opponent?${qs.toString()}`);
  if (!res || !res.ok) return null;
  try {
    const json = await res.json();
    if (!json?.snapshot) return null;
    return json.snapshot as OpponentSnapshot;
  } catch {
    return null;
  }
}

/**
 * Server-side combat sim. Server fetches BOTH snapshots from DB, runs
 * the bundled combat engine, records the result, returns the event
 * log. Returns null on any failure — caller must fall back to local
 * simulation so the round can still complete.
 */
export async function simulateCombatRemote(
  mySnapshotId: string,
  opponentSnapshotId: string,
): Promise<CombatResult | null> {
  const res = await authedFetch('/matches/simulate', {
    method: 'POST',
    body: JSON.stringify({ mySnapshotId, opponentSnapshotId }),
  });
  if (!res || !res.ok) return null;
  try {
    const json = await res.json();
    if (!json?.result) return null;
    return json.result as CombatResult;
  } catch {
    return null;
  }
}

export interface MatchResultInput {
  opponentSnapshotId: string | null;
  roundNum: number;
  outcome: 'win' | 'loss' | 'tie';
  damageDealt: number;
  damageTaken: number;
}

export async function recordMatchResult(input: MatchResultInput): Promise<boolean> {
  const res = await authedFetch('/matches/result', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return !!res?.ok;
}

export interface LadderEntry {
  rank: number;
  playerId: string;
  handle: string;
  rating: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  ties: number;
}

export interface MyRank {
  rating: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  ties: number;
  rank: number | null;
}

// Public — no auth required. Returns null on network failure so the
// caller can render a fallback / "offline" state.
export async function getLadderTop(limit = 50): Promise<LadderEntry[] | null> {
  try {
    const res = await fetch(`${apiBase()}/ladder/top?limit=${limit}`);
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json?.entries) ? (json.entries as LadderEntry[]) : null;
  } catch {
    return null;
  }
}

export async function getMyRank(): Promise<MyRank | null> {
  const res = await authedFetch('/ladder/me');
  if (!res || !res.ok) return null;
  try {
    return (await res.json()) as MyRank;
  } catch {
    return null;
  }
}

// Exposed for the account store + debugging.
export function getCachedPlayerId(): string | null {
  return readCachedAuth()?.playerId ?? null;
}

// Used when the user changes their handle in Profile — we re-auth with the
// new handle, which UPSERTs and updates the players row. Tolerant of failure.
export async function refreshHandle(handle: string): Promise<void> {
  cached = null;
  await ensureAuth(handle);
}
